import React from 'react';
import { Database, Archive, Play, RefreshCw, Lock, MapPin, FileText, Sparkles, LucideIcon } from 'lucide-react';
import Tooltip from '../../../common/Tooltip';

export type BadgeVariant = 'model' | 'registry' | 'parallel' | 'session' | 'feedback' | 'text' | 'custom';

export interface NodeBadgeProps {
  /** Icon component to display */
  icon?: React.ReactNode;
  /** Badge label text */
  label: string;
  /** Badge styling variant */
  variant?: BadgeVariant;
  /** Custom color scheme (overrides variant colors) */
  colorScheme?: string;
  /** Click handler */
  onClick?: (e: React.MouseEvent) => void;
  /** Tooltip text shown on hover */
  tooltip?: string;
  /** Priority for sorting (lower = higher priority) */
  priority?: number;
  /** Additional CSS classes */
  className?: string;
  /** Test ID for testing */
  testId?: string;
}

const variantStyles: Record<BadgeVariant, { bg: string; text: string; border?: string }> = {
  model: { bg: 'bg-indigo-100 dark:bg-indigo-900/40', text: 'text-indigo-700 dark:text-indigo-300' },
  registry: { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300' },
  parallel: { bg: 'bg-orange-100 dark:bg-orange-900/40', text: 'text-orange-700 dark:text-orange-300' },
  session: { bg: 'bg-blue-100 dark:bg-blue-900/40', text: 'text-blue-700 dark:text-blue-300', border: 'border border-primary-300 dark:border-blue-700' },
  feedback: { bg: 'bg-purple-100 dark:bg-purple-900/40', text: 'text-purple-700 dark:text-purple-300' },
  text: { bg: 'bg-green-100 dark:bg-green-900/40', text: 'text-green-700 dark:text-green-300' },
  custom: { bg: 'bg-gray-100 dark:bg-gray-800', text: 'text-gray-700 dark:text-gray-300' },
};

/**
 * Reusable badge component for workflow nodes
 * Provides consistent styling and interaction patterns across all node types
 */
const NodeBadge: React.FC<NodeBadgeProps> = ({
  icon,
  label,
  variant = 'custom',
  colorScheme,
  onClick,
  tooltip,
  className = '',
  testId,
}) => {
  const styles = variantStyles[variant];
  const baseClasses = `flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium shadow-sm transition-colors`;
  const colorClasses = colorScheme || `${styles.bg} ${styles.text} ${styles.border || ''}`;
  const interactiveClasses = onClick ? 'cursor-pointer hover:opacity-80' : '';

  const handleClick = (e: React.MouseEvent) => {
    if (onClick) {
      e.stopPropagation();
      e.preventDefault();
      onClick(e);
    }
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (onClick) {
      e.stopPropagation();
      e.preventDefault();
    }
  };

  const badgeContent = (
    <>
      {icon}
      <span className="truncate max-w-[120px]">{label}</span>
    </>
  );

  if (onClick) {
    return (
      <Tooltip content={tooltip || ''}>
        <button
          type="button"
          className={`${baseClasses} ${colorClasses} ${interactiveClasses} ${className} focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-indigo-500`}
          onClick={handleClick}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerDown}
          data-testid={testId}
        >
          {badgeContent}
        </button>
      </Tooltip>
    );
  }

  return (
    <Tooltip content={tooltip || ''}>
      <div
        className={`${baseClasses} ${colorClasses} ${className}`}
        data-testid={testId}
      >
        {badgeContent}
      </div>
    </Tooltip>
  );
};

export default NodeBadge;
